import { cn } from "@scribenote/brian-design-system";
import {
  type CardSeverity,
  severityReaderChrome,
} from "@subjects/cardSeverity";
import { formatRelativeTime } from "@utils/format-date";
import { useViewPdfExport } from "@views/useViewPdfExport";
import { ViewBody } from "@views/ViewBody";
import { ViewDeleteButton } from "@views/ViewDeleteButton";
import { ViewExportButton } from "@views/ViewExportButton";
import { viewCaption } from "@views/viewIdentity";
import type { View } from "@views/viewsClient";
import { ViewSignoffs } from "@views/ViewSignoffs";
import { ViewTaskBell } from "@views/ViewTaskBell";
import type { ReactNode } from "react";

/**
 * One view as a full reading surface (WEB-3288), shared by the subject pane and
 * the standalone view pane. The view is a document, so it gets a real sheet:
 * card paper at a reading measure, a masthead strip ruled off in ink (the same
 * head strip the estimate form wears), and its own scroll region. The severity
 * tint glazes the sheet and the accent bar rides its left edge, so status reads
 * off the paper itself. `onDeleted` lets the host move its selection off a
 * deleted view; `trailing` adds host controls (the close ✕) to the masthead.
 */
export const ViewReader = ({
  view,
  severity,
  onDeleted,
  trailing,
  lifted = false,
  withSubject = false,
}: {
  view: View;
  /** Subject-context severity from a host holding the surfaced copy. Severity
      is per-subject (BE-1918), so the by-uuid `view` always reads calm without
      this. */
  severity?: CardSeverity;
  onDeleted: () => void;
  trailing?: ReactNode;
  /** Hold the sheet further off its backdrop: `true` when it lies straight on
      the cork (the standalone pane), `false` inside the folder's manila. */
  lifted?: boolean;
  /** Name the patient in the masthead strip. False inside a subject's own pane,
      true on the standalone pane, which can be reached from anywhere. */
  withSubject?: boolean;
}) => {
  const { ref, exportPdf, exporting } = useViewPdfExport(view);
  const chrome = severityReaderChrome(severity ?? view.severity);

  // Who and which-one lead the strip, ahead of the provenance and the clock:
  // the title says only what kind of document this is (BE-2395), so on a sheet
  // opened from anywhere these are what identify the record in front of you.
  const meta = [
    viewCaption(view, { withSubject }),
    view.surfacedPosition !== null ? "Surfaced by Brian" : null,
    `updated ${formatRelativeTime(view.updatedAt)}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col p-4">
      <section
        data-testid={`view-reader-${view.id}`}
        className={cn(
          "relative mx-auto flex h-full min-h-0 w-full max-w-4xl flex-col",
          "rounded-paper border border-rule bg-card font-body text-ink-body",
          lifted ? "shadow-lift-lg" : "shadow-lift",
        )}
      >
        {/* The severity glaze and accent bar are decorative, so they get their
            own layer, clipped to the sheet's rounded corners and pinned
            behind the content with a negative z-index (not DOM order - an
            absolutely positioned box with z-index:auto still paints above
            in-flow content regardless of where it sits in the tree). Clipping
            lives here rather than on the section itself so it never catches
            ViewTaskBell's task menu, a plain absolute div (not top-layer) in
            the header below that needs room to open past the sheet's edge in
            short panes. */}
        <div
          aria-hidden
          className={cn(
            "absolute inset-0 -z-10 overflow-hidden rounded-paper",
            chrome?.surface,
          )}
        >
          {chrome && (
            <span
              className={`absolute inset-y-0 left-0 w-1.5 ${chrome.accent}`}
            />
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <header className="flex shrink-0 items-start justify-between gap-3 border-b border-ink px-7 py-5">
            <div className="min-w-0">
              <h2 className="truncate font-display text-xl font-medium text-ink">
                {view.name}
              </h2>
              <p className="mt-1 font-mono text-fine-print text-annotation">
                {meta}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <ViewTaskBell view={view} />
              <ViewExportButton
                viewId={view.id}
                onExport={exportPdf}
                exporting={exporting}
              />
              <ViewDeleteButton view={view} onDeleted={onDeleted} />
              {trailing}
            </div>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-7 py-6">
            {/* The capture region: the document's content plus, once signed,
                the stamp and sign-off trail - those are part of the record, so
                they export with it. The masthead stays out; the PDF draws its
                own branded header from `view.name`. */}
            <div ref={ref} className="flex flex-col gap-4">
              <ViewBody view={view} frameless />
              {view.status === "signed" && (
                <footer className="flex flex-col gap-3 border-t border-rule pt-5">
                  <ViewSignoffs view={view} />
                </footer>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
